import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Heart, Award, BookOpen, CheckCircle, ArrowRight, Briefcase } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { jobRoles } from '../utils/mockData';

const Recruitment = () => {
  const benefits = [
    {
      icon: Award,
      title: 'Professional Development',
      description: 'Ongoing training, clinical supervision, and career progression opportunities.'
    },
    {
      icon: Heart,
      title: 'Meaningful Work',
      description: 'Make a real difference supporting individuals with mental health needs.'
    },
    {
      icon: Users,
      title: 'Supportive Team',
      description: 'Work alongside experienced RMN-led teams in a collaborative environment.'
    },
    {
      icon: BookOpen,
      title: 'Reflective Practice',
      description: 'Regular supervision, incident learning, and evidence-based practice culture.'
    }
  ];

  const whatWeLookFor = [
    'Compassion, empathy, and person-centered approach',
    'Understanding of mental health recovery principles',
    'Commitment to safety, dignity, and professional standards',
    'Good communication and teamwork skills',
    'Willingness to learn and engage in reflective practice',
    'Relevant qualifications and registration (where applicable)'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="heading-hero">Join Our Team</h1>
            <p className="body-large">
              Be part of a passionate, RMN-led team making a real difference in mental health and community care.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-[#4A90E2] to-[#7B68BE]">
                  Apply Now
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/about">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white"
                >
                  Learn About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Why Work With Three Spheres Healthcare?</h2>
            <p className="body-large max-w-2xl mx-auto">
              We invest in our people and create a supportive, professional environment where you can grow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Current Opportunities */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Current Opportunities</h2>
            <p className="body-large max-w-2xl mx-auto">
              We're recruiting qualified, compassionate professionals to join our growing team.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {jobRoles.map((role) => (
              <Card key={role.id} className="p-8 border-0 hover:shadow-xl transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-3 mb-2">{role.title}</h3>
                    <span className="inline-block bg-[#E8F4FC] text-[#4A90E2] px-3 py-1 rounded-full text-sm font-semibold">
                      {role.type}
                    </span>
                  </div>
                  <Briefcase className="text-[#4A90E2]" size={28} />
                </div>
                <p className="body-medium text-gray-600 mb-4">{role.description}</p>
                <div className="mb-4">
                  <h4 className="font-semibold text-sm mb-3 text-gray-900">Requirements:</h4>
                  <ul className="space-y-2">
                    {role.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-0.5" size={16} />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/contact">
                  <Button 
                    variant="outline" 
                    className="w-full border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white"
                  >
                    Apply for This Role
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Look For */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] rounded-2xl p-8 md:p-12">
              <h2 className="heading-2 mb-8 text-center">What We Look For</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {whatWeLookFor.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-4">
                    <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 mb-12 text-center">Our Recruitment Process</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Apply', description: 'Submit your application via our contact form or email.' },
                { step: '2', title: 'Initial Screening', description: 'We review your qualifications and experience.' },
                { step: '3', title: 'Interview', description: 'Meet our team and discuss the role in detail.' },
                { step: '4', title: 'Onboarding', description: 'DBS check, references, training, and induction.' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Ready to Make a Difference?</h2>
            <p className="body-large text-white/90">
              Join our team and help us support individuals with mental health needs to live fulfilling lives.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Apply Today
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Recruitment;
